<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

// Scope column of the Memory browser: "all", `global`, then one row per
// project slug with its entry count. Slugs render in mono because they
// are the literal value agents pass as `project:`. A vertical column on
// wide screens, a wrapping chip row below that.

export interface MemoryScopeLink {
  slug: string
  count: number
  to: RouteLocationRaw
}

defineProps<{
  scopes: MemoryScopeLink[]
  active: string
}>()
</script>

<template>
  <nav aria-label="Memory scopes" class="flex flex-col gap-2" data-testid="memory-scope-nav">
    <h2 class="m-0 px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint max-2xl:sr-only">
      Scopes
    </h2>
    <ul class="m-0 flex list-none flex-wrap gap-1.5 p-0 2xl:flex-col 2xl:flex-nowrap 2xl:gap-0.5">
      <li v-for="scope in scopes" :key="scope.slug">
        <RouterLink
          :to="scope.to"
          :aria-current="scope.slug === active ? 'page' : undefined"
          data-testid="memory-scope"
          class="flex min-h-10 items-center gap-2 rounded-full border px-3 no-underline transition-colors max-sm:min-h-11 2xl:rounded-lg 2xl:border-transparent 2xl:px-2.5"
          :class="
            scope.slug === active
              ? 'border-brand-primary bg-surface-hover text-ink'
              : 'border-border-control text-ink-muted hover:bg-surface-hover hover:text-ink'
          "
        >
          <span class="flex-1 font-mono text-[13px]">{{ scope.slug }}</span>
          <span class="text-xs tabular-nums text-ink-faint">{{ scope.count }}</span>
        </RouterLink>
      </li>
    </ul>
    <p class="m-0 px-2.5 text-xs leading-normal text-ink-faint max-2xl:hidden">
      Agents see their repo’s scope plus global.
    </p>
  </nav>
</template>
